import React, { useState, type ComponentPropsWithRef } from "react";
import { NumericFormat } from "react-number-format";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { BaseFilterProps } from "../../lib";
import styles from "./DueDateFilter.module.scss";

const DueDaysInput = (props: Omit<ComponentPropsWithRef<"input">, "size">) => (
    <Input {...props} size="xl" clearable={false} />
);

interface DueDateOption {
    id: string;
    label: string;
    value: "any" | "overdue" | "not_due";
    filterValue: boolean | undefined;
}

const dueDateOptions: DueDateOption[] = [
    {
        id: "due-date-filter__option__any",
        label: "Any",
        value: "any",
        filterValue: undefined,
    },
    {
        id: "due-date-filter__option__overdue",
        label: "Overdue",
        value: "overdue",
        filterValue: true,
    },
    {
        id: "due-date-filter__option__not_due",
        label: "Not due yet",
        value: "not_due",
        filterValue: false,
    },
];

export const DueDateFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const [dueDays, setDueDays] = useState<{
        min?: number;
        max?: number;
    }>({
        min: filters.minDue,
        max: filters.maxDue,
    });

    const handleOptionChange = (value: DueDateOption["value"]) => {
        if (value === "any") {
            onChange({
                overdue: undefined,
                minDue: undefined,
                maxDue: undefined,
            });
        } else if (value === "not_due") {
            onChange({
                overdue: false,
                minDue: undefined,
                maxDue: undefined,
            });
        } else {
            onChange({
                overdue: true,
                minDue: undefined,
                maxDue: undefined,
            });
        }
    };

    const handleDueDaysChange = (
        type: "min" | "max",
        value: number | undefined,
    ) => {
        setDueDays((prev) => ({ ...prev, [type]: value }));

        onChange({
            overdue: false,
            minDue: type === "min" ? value : dueDays.min,
            maxDue: type === "max" ? value : dueDays.max,
        });
    };

    // Determine which option is currently selected
    const getSelectedOption = (): DueDateOption["value"] => {
        if (filters.overdue === true) {
            return "overdue";
        } else if (filters.overdue === false) {
            return "not_due";
        } else {
            return "any";
        }
    };

    const selectedOption = getSelectedOption();
    const isNotDueSelected = selectedOption === "not_due";

    return (
        <div className={styles.dueDateFilter}>
            <section>
                <header className={styles.header}>Due Date</header>
                <RadioGroup<DueDateOption["value"]>
                    name="dueDate"
                    aria-label="Due date"
                    value={selectedOption}
                    onValueChange={handleOptionChange}
                >
                    {dueDateOptions.map((option) => (
                        <Radio
                            key={option.id}
                            value={option.value}
                            label={option.label}
                        />
                    ))}
                </RadioGroup>
            </section>

            {isNotDueSelected && (
                <section>
                    <div className={styles.values}>
                        <Field label="Min days" className={styles.valueField}>
                            <NumericFormat
                                value={dueDays.min ?? undefined}
                                onValueChange={(v) =>
                                    handleDueDaysChange("min", v.floatValue)
                                }
                                customInput={DueDaysInput}
                                allowNegative={false}
                            />
                        </Field>

                        <Field label="Max days" className={styles.valueField}>
                            <NumericFormat
                                value={dueDays.max ?? undefined}
                                onValueChange={(v) =>
                                    handleDueDaysChange("max", v.floatValue)
                                }
                                customInput={DueDaysInput}
                                allowNegative={false}
                            />
                        </Field>
                    </div>
                </section>
            )}
        </div>
    );
};
